<p-dialog
  [visible]="dialog.visible()"
  [modal]="true" [draggable]="false" [resizable]="false"
  [closable]="false" [showHeader]="false"
  [style]="{ width: '520px' }"
  styleClass="branduals-dialog share-dialog"
  [appDialogEscape]="dialog.visible()"
  (escapePressed)="guardedClose()"
  (keydown)="onDialogKeydown($event)">

  <!-- PrimeNG's header is off: the dialog owns its close gesture (X, Cancel, Escape all ask before
       discarding recipients or a message), so the header row is its own. -->
  @if (dialog.visible()) {
    <app-guarded-dialog-header [title]="'share.title' | translate: { title: dialog.context()?.destinationTitle ?? '' }"
      [disabled]="sending()" (close)="guardedClose()" />
    @if (phase() === 'configure') {
      <p class="dialog-subtitle">{{ 'share.subtitle' | translate }}</p>

      <div class="form-field">
        <label>{{ 'share.peopleLabel' | translate }}</label>
        <app-chip-input #recipientsInput
            [chips]="recipients()" (chipsChange)="recipients.set($event)"
            [validator]="emailValidator" [cap]="recipientCap" defaultKind="email"
            [resolveText]="resolveTypedEmail"
            [suggestions]="suggestions()" (query)="onQuery($event)"
            [suggestionsSettledEmpty]="suggestionsSettledEmpty()"
            [noMatchLabel]="'share.noMatch' | translate"
            [invalidMessage]="'share.recipientsInvalid' | translate"
            [capMessage]="'share.recipientsCap' | translate: { cap: recipientCap }"
            [overlayHint]="'share.overlayHint' | translate"
            [placeholder]="'share.peoplePlaceholder' | translate" />
        <!-- While a typed name found nobody, the under-field hint says what to do instead. It
             YIELDS while a refusal sentence stands (stacked, the pair contradicts — "up to 100
             people" over "paste multiple addresses", or the invite advice under a refused email);
             typing clears the refusal, so the hint returns with the next keystroke. -->
        @if (!recipientsInput.refusalStanding()) {
          <p class="field-hint">{{ (suggestionsFailed() ? 'share.peopleSearchDown' : suggestionsSettledEmpty() ? 'share.overlayHint' : 'share.pasteHint') | translate }}</p>
        }
      </div>

      <div class="form-field">
        <label>{{ 'share.messageLabel' | translate }}</label>
        <textarea pTextarea rows="3" [attr.maxlength]="messageMaxLength"
            [value]="message()" (input)="message.set($any($event.target).value)"
            [placeholder]="'share.messagePlaceholder' | translate"></textarea>
        <!-- The ceiling, said as the member nears it — a pasted long note is never cut without a word. -->
        <app-char-counter [left]="messageMaxLength - message().length"/>
      </div>

      @if (temporal.active()) {
        <p class="asof-note">
          <fa-duotone-icon [icon]="faClockRotateLeft" />
          <span>{{ 'share.asOfNote' | translate: { date: (temporal.asOf() | displayDate:'dateTime') } }}</span>
        </p>
      }

      @if (disclosureName()) {
        <p class="disclosure-line">
          <fa-duotone-icon [icon]="faCircleInfo" />
          <span>{{ disclosureKey() | translate: { name: disclosureName() } }}</span>
        </p>
      }
    } @else if (phase() === 'done') {
      <div class="share-done">
        <fa-duotone-icon [icon]="faCheck" class="done-icon" />
        @if (anythingSent()) {
          <h4 class="done-title">{{ 'share.doneTitle' | translate }}</h4>
          <p class="done-body">{{ 'share.doneBody' | translate: { brand: brandName() } }}</p>
        }
        @if (alreadyWaiting().length > 0) {
          <p class="done-body waiting-note">{{ alreadyWaitingSummary() }}</p>
          @if (alreadyWaiting().length > 1) {
            <ul class="waiting-list">
              @for (email of alreadyWaiting(); track email) {
                <li>{{ email }}</li>
              }
            </ul>
          }
        }
        <button type="button" class="done-link" (click)="viewSharesSent()">
          {{ 'share.viewSharesSent' | translate }}
        </button>
      </div>
    } @else {
      <div class="share-failure">
        <!-- The summary promises a retry, so it shows only while someone is left to send again. When someone new was
             delivered and the plan's member limit alone stopped the rest, nobody is left to retry and the face below
             explains the refusals, so one line still says what the summary would have: the others were delivered. -->
        @if (retryable()) {
          <p class="failure-summary">
            {{ (phase() === 'partial' ? 'share.partialSummary' : 'share.failedSummary') | translate }}
          </p>
        } @else if (phase() === 'partial') {
          <p class="delivered-note" data-note="delivered">{{ 'share.othersDelivered' | translate }}</p>
        }
        @if (failureMessages().length) {
          <ul class="failure-list">
            @for (m of failureMessages(); track m) {
              <li>{{ m }}</li>
            }
          </ul>
        }
        @if (seatRefusedChips().length > 0) {
          <!-- The people the brand had no room for: the one limit face speaks for them, ONCE for the batch — its members
               sentence and its doors by the sharer's standing — and they are listed under it, never retried. The dialog is
               mounted once at the app root and outlives every route change, so the face's "See plans" closes it on the
               way; the face stands only in a result, which Close also leaves without asking, a retry still owed included. -->
          <app-plan-limit kind="MEMBERS" mode="inline" (seePlans)="dialog.close()" />
          <ul class="waiting-list seat-refused-list">
            @for (chip of seatRefusedChips(); track chip.key) {
              <li>{{ chip.label }}</li>
            }
          </ul>
        }
        @if (alreadyWaiting().length > 0) {
          <!-- A true outcome, not a failure: these people were already waiting for
               approval — nothing new was sent to them, whatever happened to the rest. -->
          <p class="waiting-note">{{ alreadyWaitingSummary() }}</p>
          @if (alreadyWaiting().length > 1) {
            <ul class="waiting-list">
              @for (email of alreadyWaiting(); track email) {
                <li>{{ email }}</li>
              }
            </ul>
          }
        }
      </div>
    }
  }

  <!-- Single stable footer template — its CONTENT switches on phase(). A footer nested inside
       a phase @if is captured once by PrimeNG's ContentChildren query and never re-resolved. -->
  <ng-template pTemplate="footer">
    @if (phase() === 'configure') {
      <button pButton type="button" class="p-button-outlined copy-link-btn" (click)="copyLink()"
          [pTooltip]="copyLinkScope().tooltip | translate" tooltipPosition="top">
        <fa-duotone-icon [icon]="faLink"></fa-duotone-icon>
        {{ 'common.copyLink' | translate }}
      </button>
      <span class="footer-spacer"></span>
      <button pButton type="button" class="p-button-text" (click)="guardedClose()">
        {{ 'common.cancel' | translate }}
      </button>
      <app-submit-chord-hint verb="send" />
      <button pButton type="button" class="p-button-outlined send-btn"
          [loading]="sending()" [disabled]="!canSend()" (click)="send()">
        {{ 'share.send' | translate }}
      </button>
    } @else if (phase() === 'done') {
      <button pButton type="button" class="p-button-outlined" (click)="guardedClose()">
        {{ 'common.done' | translate }}
      </button>
    } @else {
      <button pButton type="button" class="p-button-text" (click)="guardedClose()">
        {{ 'common.close' | translate }}
      </button>
      @if (retryable()) {
        <button pButton type="button" class="p-button-outlined retry-btn" (click)="retry()">
          {{ 'common.retry' | translate }}
        </button>
      }
    }
  </ng-template>
</p-dialog>

<!-- The discard prompt stacks above the dialog — and above the file preview it may have opened
     from — through the styleClass's z-lift row, one rung above the dialog's own lift. -->
<p-confirmDialog styleClass="discard-confirm" />
